import { useCallback, useEffect, useMemo } from 'react';
import { Col, Form, Row } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from 'components';
import { EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { DeleteFilled, EyeOutlined } from '@ant-design/icons';
import ErrorHelper from 'helpers/ErrorHelper';
import PrestigeLevelsAPI from 'libs/api/admin/prestigeLevels';
import { useApi } from 'hooks/useApi';
import { useNavigate } from 'hooks/useNavigate';
import { useRecoilValue } from 'recoil';
import { getPage } from 'hooks/useUser';

const api = new PrestigeLevelsAPI();

const usePrestigeLevels = (isMainPage = true) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const pages = useRecoilValue(getPage);

  const {
    data,
    singleItem: singleData,
    loading,
    onChangeTableFilter,
    onDelete: handleDeleteCategory,
    updateInProcess,
    setUpdateInProcess
  } = useApi({
    resourceKey: 'prestige_levels',
    singleResourceKey: 'prestige_level',
    api,
    isMainPage
  });

  const filters = useMemo(() => {
    return [
      {
        title: t('Titolo'),
        type: 'input',
        name: 'by_title',
      },
    ];
  }, [t]);

  const columns = useMemo(() => {
    return [
      {
        title: t('Titolo'),
        dataIndex: 'title',
        width: 100,
        sorter: () => {},
      },
      {
        title: t('NEGO ID (TOV)'),
        dataIndex: 'nego_tov_id',
        width: 100,
        sorter: () => {},
      },
      {
        title: t('NEGO ID (SOV)'),
        dataIndex: 'nego_sov_id',
        width: 100,
        sorter: () => {},
      },
      {
        title: t('NEGO ID (ROV)'),
        dataIndex: 'nego_rov_id',
        width: 100,
        sorter: () => {},
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 160,
        render: (data, row) =>
          (pages?.ability?.can_read !== false ||
            row.can_read !== false ||
            row.can_update !== false ||
            row.can_destroy !== false) && (
            <Row gutter={16} justify={'end'}>
              {(pages?.ability?.can_read !== false ||
                row.can_read !== false) && (
                <Col>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type={'secondary'} icon={<EyeOutlined />} />
                  </Link>
                </Col>
              )}
              {row.can_update !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
              {row.can_destroy !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/delete`}>
                    <Button type={'red'} icon={<DeleteFilled />} />
                  </Link>
                </Col>
              )}
            </Row>
          ),
      },
    ];
  }, [t, pathname, pages?.ability?.can_read]);

  const handleCreateOrUpdate = useCallback(async values => {
    setUpdateInProcess(true);
    try {
      const data = {
        prestige_level: {
          ...values,
        },
      };
      if (!id) {
        await api.create({ data });
        navigate('/admin/prestige_levels');
      } else {
        await api.put(id, { data });
        navigate(`/admin/prestige_levels/${id}?success=true`);
      }
    } catch (e) {
      form.setFields(ErrorHelper.getErrorsForForm(e?.response?.data || {}));
    } finally {
      setUpdateInProcess(false);
    }
  }, [form, navigate, setUpdateInProcess, id]);

  useEffect(() => {
    if (id && !isMainPage && singleData) {
      try {
        let fieldsValues = [];
        for (let key in singleData) {
          if (key !== 'id') {
            fieldsValues.push({
              name: key,
              value: singleData[key],
            });
          }
        }
        form.setFields(fieldsValues);
      } catch (e) {
        console.log(e);
      }
    }
  }, [id, isMainPage, singleData, form]);

  return {
    columns,
    data,
    singleData,
    loading,
    updateInProcess,
    onChangeTableFilter,
    form,
    filters,
    handleCreateOrUpdate,
    handleDeleteCategory,
  };
};

export default usePrestigeLevels;